import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DealCard = ({ deal, onSaveDeal, onShareDeal }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date()?.getTime();
      const endTime = new Date(deal.endTime)?.getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
          setTimeLeft(`${days}d ${hours}h`);
        } else if (hours > 0) {
          setTimeLeft(`${hours}h ${minutes}m`);
        } else {
          setTimeLeft(`${minutes}m`);
        }
      } else {
        setTimeLeft('Expired');
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);

    return () => clearInterval(timer);
  }, [deal?.endTime]);

  const handleSave = () => {
    setIsSaved(!isSaved);
    onSaveDeal(deal?.id);
  };

  const handleShare = () => {
    onShareDeal(deal);
  };

  const discountPercentage = Math.round(((deal?.originalPrice - deal?.currentPrice) / deal?.originalPrice) * 100);

  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-floating transition-all duration-300 group">
      {/* Deal Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            deal?.dealType === 'flash' ?'bg-destructive text-destructive-foreground' 
              : deal?.dealType === 'seasonal' ?'bg-warning text-warning-foreground' :'bg-success text-success-foreground'
          }`}>
            {deal?.dealType === 'flash' ? 'Flash Sale' : deal?.dealType === 'seasonal' ? 'Seasonal' : 'Best Price'}
          </span>
          {deal?.isExclusive && (
            <span className="px-2 py-1 bg-accent text-accent-foreground rounded-full text-xs font-medium">
              Exclusive
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSave}
            className={`w-8 h-8 ${isSaved ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            <Icon name={isSaved ? "Heart" : "Heart"} size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShare}
            className="w-8 h-8 text-muted-foreground hover:text-primary"
          >
            <Icon name="Share2" size={16} />
          </Button>
        </div>
      </div>
      {/* Product Image */}
      <div className="relative mb-4 overflow-hidden rounded-lg bg-muted">
        <Image
          src={deal?.image}
          alt={deal?.imageAlt}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3 bg-destructive text-destructive-foreground px-2 py-1 rounded-md text-sm font-bold">
          -{discountPercentage}%
        </div>
      </div>
      {/* Product Info */}
      <div className="mb-4">
        <h3 className="font-semibold text-lg text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {deal?.title}
        </h3>
        <div className="flex items-center space-x-2 mb-2">
          <div className="flex items-center">
            {[...Array(5)]?.map((_, i) => (
              <Icon
                key={i}
                name="Star"
                size={14}
                className={i < Math.floor(deal?.rating) ? 'text-warning fill-current' : 'text-muted-foreground'}
              />
            ))}
          </div>
          <span className="text-sm text-muted-foreground">({deal?.reviewCount})</span>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {deal?.description}
        </p>
      </div>
      {/* Pricing */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-1">
          <span className="text-2xl font-bold text-primary">${deal?.currentPrice}</span>
          <span className="text-lg text-muted-foreground line-through">${deal?.originalPrice}</span>
        </div>
        <div className="text-sm text-success font-medium">
          You save ${(deal?.originalPrice - deal?.currentPrice)?.toFixed(2)}
        </div>
      </div>
      {/* Timer */}
      <div className="mb-4 p-3 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-destructive" />
            <span className="text-sm font-medium text-text-primary">Deal ends in:</span>
          </div>
          <span className={`font-bold ${timeLeft === 'Expired' ? 'text-muted-foreground' : 'text-destructive'}`}>
            {timeLeft}
          </span>
        </div>
      </div>
      {/* Actions */}
      <div className="space-y-2">
        <Button
          variant="default"
          fullWidth
          iconName="ExternalLink"
          iconPosition="right"
          disabled={timeLeft === 'Expired'}
          className="font-semibold"
        >
          {timeLeft === 'Expired' ? 'Deal Expired' : 'Get This Deal'}
        </Button>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="BarChart3"
            iconPosition="left"
            className="flex-1"
          >
            Price History
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="GitCompare"
            iconPosition="left"
            className="flex-1"
          >
            Compare
          </Button>
        </div>
      </div>
      {/* Store Info */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <Image
              src={deal?.store?.logo}
              alt={deal?.store?.logoAlt}
              className="w-6 h-6 rounded"
            />
            <span className="text-muted-foreground">{deal?.store?.name}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Truck" size={14} className="text-success" />
            <span className="text-success text-xs">{deal?.shipping}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealCard;